/* Geometry for the glow that tracks the pointer around the user-menu pill.
   Shared by the company and employee portal headers — pure math, no
   framework dependency, so it lives here rather than duplicated per app. */

export const GLOW_EASING = 0.22;
export const GLOW_SETTLE = 0.5;
// Ceiling on the eased step, in px per frame: easing alone is proportional to
// the distance left, so crossing between the edges — half the perimeter — whips
// around the cap, while a nudge along one edge crawls.
export const GLOW_MAX_STEP = 9;

const reducedMotionQuery =
  typeof window === 'undefined'
    ? null
    : window.matchMedia('(prefers-reduced-motion: reduce)');

// `matches` stays live, so the cached list still tracks a preference change —
// without a `matchMedia` call on every pointer move.
export const prefersReducedMotion = () => reducedMotionQuery?.matches ?? false;

export const glowPerimeter = (width: number, height: number) =>
  2 * Math.max(width - height, 0) + Math.PI * height;

export const glowDistanceFor = (
  pointerX: number,
  width: number,
  height: number,
  isTopEdge: boolean
) => {
  const radius = height / 2;
  const straight = Math.max(width - height, 0);
  const x = Math.min(Math.max(pointerX, radius), width - radius);

  return isTopEdge
    ? x - radius
    : straight + Math.PI * radius + (width - radius - x);
};

export const glowPointAt = (
  distance: number,
  width: number,
  height: number
) => {
  const radius = height / 2;
  const straight = Math.max(width - height, 0);
  const cap = Math.PI * radius;

  if (distance < straight) {
    return { x: radius + distance, y: 0 };
  }

  if (distance < straight + cap) {
    const angle = (distance - straight) / radius;
    return {
      x: width - radius + radius * Math.sin(angle),
      y: radius - radius * Math.cos(angle),
    };
  }

  if (distance < 2 * straight + cap) {
    return { x: width - radius - (distance - straight - cap), y: height };
  }

  const angle = (distance - 2 * straight - cap) / radius;
  return {
    x: radius - radius * Math.sin(angle),
    y: height - radius + radius * Math.cos(angle),
  };
};
